import { html, View } from 'rune-ts';
import klass from './CreatorSearchSectionView.module.scss';
import { getResizedImageUrl } from '@services/MarppleShop/shared/util/getResizedImageUrl.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { SearchModuleCreatorAll } from '@services/MarppleShop/renderApp/components/cells/SearchModule/SearchModuleCreatorAll.ts';
import { AutoCompleteListOption } from '@services/MarppleShop/renderApp/components/cells/SearchModule/view/AutoCompleteList.ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { htmlIf } from '@services/MarppleShop/shared/util';
import anime from 'animejs';
import { Input } from '@services/MarppleShop/renderApp/components/atoms/Input/Input.ts';

export type CreatorSearchSectionViewData = {
  count: number;
  value?: string;
};

type CreatorSearchSectionViewOption = Pick<AutoCompleteListOption, 'highlight'> & {
  klass?: string;
  without_visible_interaction?: boolean;
  is_mobile: boolean;
  search_input_left_part?: View;
};

export class CreatorSearchSectionView extends View<CreatorSearchSectionViewData> {
  public fakeInputView: Input;
  public searchModuleCreatorAll: SearchModuleCreatorAll;
  public creatorSearchModal: Modal;

  constructor(
    data: CreatorSearchSectionViewData,
    private readonly option: CreatorSearchSectionViewOption,
  ) {
    super(data);

    const { texts } = this;

    this.fakeInputView = new Input({
      name: 'fake_input',
      value: data.value,
      placeholder: texts.place_holder,
      readonly: true,
    });

    this.searchModuleCreatorAll = new SearchModuleCreatorAll(
      {
        value: data.value,
        placeholder: texts.place_holder,
      },
      { is_mobile: this.option.is_mobile, highlight: this.option.is_mobile ? 'stroke' : 'fill' },
    );

    this.creatorSearchModal = new Modal({
      size: 'large',
      body: this.searchModuleCreatorAll,
      isOpen: false,
      full_width: true,
    });
  }

  protected override template() {
    const { texts } = this;

    return html`
      <div class="${klass.creator_search}">
        <div class="${klass.bg}">
          <img src="${this.makeBgHtml()}" alt="마럽이" />
        </div>

        <div class="${klass.title_wrapper}">
          <div class="${klass.title} ${font('16_medium', '14_medium')}">${texts.title}</div>
          <div class="${klass.sub_title} ${font('32_bold', '24_bold')}">
            ${this.formatNumber(Math.floor(this.data.count / 2))}${html.preventEscape(texts.sub_title)}
          </div>
        </div>

        <div class="${klass.input_section}">
          <div class="${klass.search_input}">${this.fakeInputView}</div>

          ${htmlIf(this.searchModuleCreatorAll, !this.option.is_mobile)}
        </div>

        ${htmlIf(this.creatorSearchModal, this.option.is_mobile)}
      </div>
    `;
  }

  protected override onRender() {
    if (!this.option.is_mobile) {
      document.addEventListener('mousedown', this.handleClickOutside);
    }

    this.delegate('click', `.${klass.search_input}`, () => {
      if (this.option.is_mobile) {
        this.creatorSearchModal.open();
      }
      this.searchModuleCreatorAll.open();
    });

    this.delegate(SearchModuleCreatorAll.CloseEvent, SearchModuleCreatorAll, (e) => {
      const { detail } = e;

      this.fakeInputView.setValue(detail);

      if (this.option.is_mobile) {
        this.creatorSearchModal.close();
      }
    });

    this.startCountAnimation();
  }

  private startCountAnimation() {
    const sub_title$ = this.element().querySelector<HTMLElement>(`.${klass.sub_title}`);

    if (sub_title$) {
      this.animateCountUp(sub_title$, this.data.count / 2, this.data.count, this.texts.sub_title);
    }
  }

  private handleClickOutside = (event: MouseEvent | TouchEvent) => {
    const section$ = this.searchModuleCreatorAll.element();

    if (!section$?.contains(event.target as Node)) {
      this.fakeInputView.setValue(this.searchModuleCreatorAll.close());
    }
  };

  private makeBgHtml() {
    return getResizedImageUrl({
      url: '//s3.marpple.co/files/u_4648126/2025/1/original/bae97ba5d61368ba9bff0a71f2f54a59dd32af3a37.webp',
      quality: 85,
      width: 340 * 2,
      bg: '#1c75ff',
    });
  }

  private get texts() {
    return {
      title: ET('mps2::creator_search::search_module::title'),
      sub_title: this.option.is_mobile
        ? ET('mps2::creator_search::search_module::sub_title_mo')
        : ET('mps2::creator_search::search_module::sub_title'),
      place_holder: ET('mps2::creator_search::search_module::place_holder'),
      recent_search: ET('mps2::creator_search::search_module::recent_search'),
      delete_auto_completes: ET('mps2::creator_search::search_module::delete_auto_completes'),
    };
  }

  private formatNumber(count: number) {
    return UtilS.commify(count);
  }

  private animateCountUp(targetElement: HTMLElement, start: number, end: number, sub_title: string): void {
    const obj = { value: start };

    anime({
      targets: obj,
      value: end,
      duration: 2000,
      round: 1,
      easing: 'easeOutExpo',
      update: () => {
        targetElement.innerHTML = `${this.formatNumber(obj.value)}${sub_title}`;
      },
    });
  }
}
